<template>
  <v-container>
    <v-row
      no-gutters
      justify="center"
    >
      <v-col xl="9">
        <div class="headline">
          Navigare Margins Management
        </div>

        <!--<editor-fold desc="Inventory margins margins">-->
        <div
          v-if="$can('edit-inventory-margin', 'Country')"
          class="subtitle-1 grey--text text--darken-2 mt-8"
        >
          Inventory Parts Margin & Hourly Rates
        </div>
        <div class="body-2 grey--text">
          All prices exclude VAT
        </div>

        <v-card
          v-if="$can('edit-inventory-margin', 'Country')"
          class="elevation-5 mt-5"
        >
          <v-row
            no-gutters
            align="center"
            class="list--bordered"
          >
            <v-col
              cols="5"
              class="pa-4"
            />
            <v-col class="pa-4 caption font-weight-bold">
              Inventory parts margin
            </v-col>
            <v-col class="pa-4 caption font-weight-bold">
              Billable hour price
            </v-col>
            <v-col class="pa-4 caption font-weight-bold">
              Internal hour cost
            </v-col>
          </v-row>
          <v-row
            v-for="country in countriesDirty"
            :key="country.id"
            no-gutters
            class="list--bordered"
            align="center"
          >
            <v-col
              cols="5"
              class="pa-4 grey--text text--darken-2 text-body-2"
            >
              {{ country.name }}
            </v-col>
            <v-col class="pa-4">
              <currency-input
                v-model="country.inventory_margin_rate"
                outlined
                dense
                suffix="%"
                hide-details
              />
            </v-col>
            <v-col class="pa-4">
              <currency-input
                v-model="country.hour_cost"
                outlined
                dense
                :suffix="country.currency"
                hide-details
              />
            </v-col>
            <v-col class="pa-4">
              <currency-input
                v-model="country.internal_hour_cost"
                outlined
                dense
                :suffix="country.currency"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row
            class="list--bordered"
            no-gutters
          >
            <v-col
              offset="5"
              cols="auto"
              class="pa-4"
            >
              <v-btn
                color="primary"
                :loading="savingInventoryMargins"
                @click="saveInventoryMargins()"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-check
                </v-icon>
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Currency exchange rate margins">-->
        <v-row
          align="center"
          class="mt-8"
        >
          <v-col>
            <div class="subtitle-1 grey--text text--darken-2">
              Currency Exchange Rate Margins
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn
              v-if="$can('create', 'CurrencyExchangeMarginRate')"
              color="primary"
              class="ma-0"
              @click="openCurrencyExchangeMarginRateEditDialog()"
            >
              <v-icon left>
                add
              </v-icon>
              Add New
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="currencyExchangeMarginRates.headers"
          :items="currencyExchangeMarginRates.data"
          hide-default-footer
          class="elevation-5 mt-5"
        >
          <template v-slot:item="{ item }">
            <currency-exchange-margin-rate-row
              :currency-exchange-margin-rate="item"
              @click.native="openCurrencyExchangeMarginRateEditDialog(item)"
            />
          </template>
          <template v-slot:no-data>
            <!--<editor-fold desc="Loader">-->
            <v-row
              v-if="currencyExchangeMarginRates.loading"
              class="justify-center align-center pa-6"
            >
              <v-col
                class="pa-6 shrink"
              >
                <v-progress-circular
                  :size="50"
                  :width="4"
                  indeterminate
                />
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-else-if="isFiltered"
              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else
              class="text-center py-2"
            >
              <v-col cols="16">
                There are no currency exchange rate margins.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'CurrencyExchangeMarginRate')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openCurrencyExchangeMarginRateEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
        </v-data-table>

        <currency-exchange-margin-rate-edit-dialog
          :currency-exchange-margin-rate="currencyExchangeMarginRateDirty"
          :visible.sync="currencyExchangeMarginRateEditDialog"
          @update:currencyExchangeRate="fetchCurrencyExchangeMarginRates"
        />
        <!--</editor-fold>-->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'
import CurrencyExchangeMarginRate from '@/models/CurrencyExchangeMarginRate'
import CurrencyExchangeMarginRateRow from '@/components/settings/currencyExchangeRates/CurrencyExchangeMarginRateRow'
import CurrencyExchangeMarginRateEditDialog
  from '@/components/settings/currencyExchangeRates/CurrencyExchangeMarginRateEditDialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import Country from '@/models/Country'
import Big from 'big.js'

export default {
    name: 'MarginsIndex',
    components: {
      CurrencyInput,
      CurrencyExchangeMarginRateRow,
      CurrencyExchangeMarginRateEditDialog
    },
    data() {
      return {
        currencyExchangeMarginRates: {
          data: [],
          loading: false,
          headers: [
            { text: 'Years in Future', value: 'years_in_future', sortable: true },
            { text: 'Margin', value: 'rate', sortable: false }
          ]
        },
        currencyExchangeMarginRateDirty: undefined,
        currencyExchangeMarginRateEditDialog: false,
        countriesDirty: [],
        savingInventoryMargins: false
      }
    },
    computed: {
      currencies() {
        return CurrencyExchangeRate.currencies
      },
      currencyExchangeHeaders() {
        return [
          { text: 'Base', value: 'base', sortable: false },
          { text: 'Quote', value: 'quote', sortable: false },
          { text: 'Market Rate', value: 'rate', sortable: false },
          { text: 'NY Rate ' + this.nextYear, value: 'rate', sortable: false },
          { text: 'Date', value: 'date', sortable: false }
        ]
      }
    },
    created() {
      this.fetchCurrencyExchangeMarginRates()
      this.loadCountries()

      this.nextYear = (new Date().getFullYear() + 1)
    },
    methods: {
      async loadCountries() {
        try {
          const result = await Country.api.fetch()

          this.countriesDirty = result.filter((country) => country.chartering).map(country => {
            return {
              ...country,
              inventory_margin_rate: country.inventory_margin_rate ? Number(Big(country.inventory_margin_rate).times(100).round(2)) : null
            }
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            color: 'error',
            text: 'Something went wrong loading countries margin data'
          })
          throw e
        }
      },
      async fetchCurrencyExchangeMarginRates() {
        this.currencyExchangeMarginRates.loading = true

        try {
          this.currencyExchangeMarginRates.data = await CurrencyExchangeMarginRate.api.fetch()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.currencyExchangeMarginRates.loading = false
        }
      },
      openCurrencyExchangeMarginRateEditDialog(currencyExchangeMarginRate = undefined) {
        if (!this.$can('edit', 'CurrencyExchangeMarginRate')) return

        this.currencyExchangeMarginRateDirty = cloneDeep(currencyExchangeMarginRate)
        this.currencyExchangeMarginRateEditDialog = true
      },
      async saveInventoryMargins() {
        this.savingInventoryMargins = true

        try {
          const data = {
            countries: this.countriesDirty.map(country => {
              return {
                id: country.id,
                inventory_margin_rate: country.inventory_margin_rate ? Big(country.inventory_margin_rate).div(100).round(4).toString() : null,
                hour_cost: country.hour_cost,
                internal_hour_cost: country.internal_hour_cost
              }
            })
          }

          await Country.api.updateMargins(data)

          this.$store.dispatch('alert', {
            color: 'success',
            text: 'Margins successfully saved'
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            color: 'error',
            text: 'Something went wrong'
          })
          throw e
        } finally {
          this.savingInventoryMargins = false
        }
      }
    }
  }
</script>
